import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CardWrapper,
  CardHeader,
  TitleBar,
  CardDescription,
  CardBottomSection,
  TabDisplay,
  AddIconDiv,
  ResponsiveButtonSection,
  TaskTitle
} from './styles';
import Button from '../button';
import { rootStore } from '../../mobx/rootStore';
import FilterDropdowns from './filterDropdowns';
import { Dropdown } from '../dropdown';
import AddIconDropdown from './addIconDropdown';
import DisplayFilterDropdown from '../displayFilterDropdown';
import { observer } from 'mobx-react-lite';
import { dispatchProjectError } from '../../services/projectServices';
import FilterChips from './filterChips';
import { createNewView, dispatchViewSuccess, getViewList, updateView } from '../../services/viewServices';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { VIEWS_FILTER_TYPE } from '../../mobx/interfaces/view';
import { updateWorkspaceViewPreference } from '../../helpers/firebaseHelper';
import { nanoid } from 'nanoid';
import { UserModel } from '../../mobx/models/user';
import { BUTTON_TYPE, CHIP_SIZE, CHIP_TYPE } from '../../global/constants';
import { useMobileDevice } from '../../global/useMobile';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import Chip from '../chipComponent';
import { ROUTES } from '../../routes/urls';

const ViewCard: React.FC = () => {
  //State variables
  const [isEditMode, setIsEditMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isTaskFilterOpen, setIsTaskFilterOpen] = useState(false);
  const isMobile = useMobileDevice();

  // ref variable
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const dropdownRef = useRef<{ closeDropdown: () => void }>(null);
  const titleTextareaRef = useRef<HTMLTextAreaElement>(null);

  //Mobx store variables
  const { viewStore, workspaceStore } = rootStore;
  const { createViewInput, currentView } = viewStore;
  const { currentWorkspace } = workspaceStore;
  const { viewId } = useParams<{ viewId: string }>();

  //other variables
  const history = useHistory();
  const location = useLocation();

  const isEdit = useMemo(
    () => new URLSearchParams(location.search).get('isEdit') === 'true' || viewId === 'new-view',
    [location.search, viewId]
  );

  const loadProjectData = useCallback(async () => {
    if (!viewId || !currentWorkspace?.id) return;
    viewStore.setCurrentView(viewId);
    if (currentView && isEdit && viewId !== 'new-view') {
      createViewInput.updateWith(currentView);
    } else {
      createViewInput.setId(nanoid());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId, currentWorkspace?.id, viewStore, currentView, isEdit]);

  // Auto-resize textarea when value changes and on mount / device change
  useEffect(() => {
    if (titleTextareaRef.current) {
      const textarea = titleTextareaRef.current;
      textarea.style.height = isMobile ? '32px' : '26px';
      const scrollHeight = textarea.scrollHeight;
      if (scrollHeight > (isMobile ? 32 : 26)) {
        textarea.style.height = `${scrollHeight}px`;
      }
    }
  }, [createViewInput.name, isMobile]);

  useEffect(() => {
    loadProjectData();
    return () => {
      createViewInput.clear();
    };
  }, [loadProjectData, createViewInput]);

  const isFilterChanged = useCallback(() => {
    const isData = Object.values(createViewInput.filters || {}).some((filter) => {
      const firstValue = Object.values(filter)[0];
      return Array.isArray(firstValue) ? firstValue.length > 0 : !!firstValue;
    });
    setIsEditMode(isData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createViewInput.filters.assign,
    createViewInput.filters.status,
    createViewInput.filters.priority,
    createViewInput.filters.project,
    createViewInput.filters.customer,
    createViewInput.filters.dueDate
  ]);

  useEffect(() => {
    isFilterChanged();
  }, [isFilterChanged]);

  const onCreateView = useCallback(async () => {
    try {
      setLoading(true);
      if (!createViewInput.name) {
        dispatchProjectError('Please enter a name for the view');
        return;
      }
      const payload = {
        name: createViewInput.name,
        description: createViewInput.description || createViewInput.generatedDescription,
        id: createViewInput.id,
        icon: createViewInput.icon,
        color: createViewInput.color,
        userIds: createViewInput.userIds,
        filters: createViewInput?.filters
      };

      const response = await createNewView(payload);
      if (response) {
        dispatchViewSuccess('View created successfully');
        await updateWorkspaceViewPreference(response?.['_id'], { ...createViewInput?.displayFilters });
        await getViewList();
        history.push(ROUTES.VIEW_ROUTE.replace(':viewId', response?.['_id']));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  }, [createViewInput, history]);

  const onChangeAssign = useCallback(
    (user: UserModel) => {
      const isChecked = createViewInput?.filters?.assign?.assignees.includes(user.id);
      let updatedAssign = createViewInput?.filters?.assign?.assignees;
      if (isChecked) {
        updatedAssign = updatedAssign.filter((assign) => assign !== user.id);
      } else {
        updatedAssign = [...updatedAssign, user.id];
      }
      createViewInput.setAssignFilter(updatedAssign, createViewInput?.filters?.assign?.type);
    },
    [createViewInput]
  );

  const onChangeUnassign = useCallback(
    (unassigned: string) => {
      const isChecked = createViewInput?.filters?.assign?.assignees.includes(unassigned);
      let updatedAssign = createViewInput?.filters?.assign?.assignees;
      if (isChecked) {
        updatedAssign = updatedAssign.filter((assign) => assign !== unassigned);
      } else {
        updatedAssign = [...(updatedAssign || []), unassigned];
      }
      createViewInput.setAssignFilter(updatedAssign, createViewInput?.filters?.assign?.type);
    },
    [createViewInput]
  );

  const onChangeStatus = useCallback(
    (type: number) => {
      const isChecked = createViewInput?.filters?.status?.statuses.includes(type);
      let updatedStatus = createViewInput?.filters?.status?.statuses;
      if (isChecked) {
        updatedStatus = updatedStatus.filter((status) => status !== type);
      } else {
        updatedStatus = [...updatedStatus, type];
      }
      createViewInput.setStatusFilter(updatedStatus, createViewInput?.filters?.status?.type);
      // Close dropdown after selection
      dropdownRef.current?.closeDropdown();
    },
    [createViewInput]
  );

  const onChangePriority = useCallback(
    (type: number) => {
      const isChecked = createViewInput?.filters?.priority?.priorities.includes(type);
      let updatedPriority = createViewInput?.filters?.priority?.priorities;
      if (isChecked) {
        updatedPriority = updatedPriority.filter((priority) => priority !== type);
      } else {
        updatedPriority = [...updatedPriority, type];
      }
      createViewInput.setPriorityFilter(updatedPriority, createViewInput?.filters?.priority?.type);
      // Close dropdown after selection
      dropdownRef.current?.closeDropdown();
    },
    [createViewInput]
  );

  const onChangeProject = useCallback(
    (id: string) => {
      const isChecked = createViewInput?.filters?.project?.projects.includes(id);
      let updatedProject = createViewInput?.filters?.project?.projects;
      if (isChecked) {
        updatedProject = updatedProject.filter((project) => project !== id);
      } else {
        updatedProject = [...updatedProject, id];
      }
      createViewInput.setProjectFilter(updatedProject, createViewInput?.filters?.project?.type);
      // Close dropdown after selection
      dropdownRef.current?.closeDropdown();
    },
    [createViewInput]
  );
  const onChangeCustomer = useCallback(
    (customer: CustomerDetailsInterface) => {
      const isChecked = createViewInput?.filters?.customer?.customers.includes(customer.id);
      let updatedCustomer = createViewInput?.filters?.customer?.customers;
      if (isChecked) {
        updatedCustomer = updatedCustomer.filter((customerId) => customerId !== customer.id);
      } else {
        updatedCustomer = [...(updatedCustomer || []), customer.id];
      }
      createViewInput.setCustomerFilter(updatedCustomer, createViewInput?.filters?.customer?.type);
      // Close dropdown after selection
      dropdownRef.current?.closeDropdown();
    },
    [createViewInput]
  );
  const onChangeDueDate = useCallback(
    (dates: string | string[]) => {
      if (Array.isArray(dates) && dates.length === 2) {
        // Custom range
        createViewInput.setDueDateFilter(dates, VIEWS_FILTER_TYPE.RANGE);
      } else {
        const date = Array.isArray(dates) ? dates[0] : dates;
        if (date && date !== 'In the past' && date !== 'No due date') {
          createViewInput.setDueDateFilter([], VIEWS_FILTER_TYPE.RANGE);
        }

        if (date === 'In the past' || date === 'No due date') {
          if (
            createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS ||
            createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS_NOT
          ) {
            createViewInput.setDueDateFilter([date], createViewInput?.filters?.dueDate?.type);
          } else {
            createViewInput.setDueDateFilter([date], VIEWS_FILTER_TYPE.IS);
          }
        } else {
          if (
            createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS ||
            createViewInput?.filters?.dueDate?.type === VIEWS_FILTER_TYPE.IS_NOT
          ) {
            createViewInput.setDueDateFilter([date], VIEWS_FILTER_TYPE.BEFORE);
          } else {
            createViewInput.setDueDateFilter([date], createViewInput?.filters?.dueDate?.type);
          }
        }
      }
      // Close dropdown after selection
      dropdownRef.current?.closeDropdown();
    },
    [createViewInput]
  );

  const onUpdateView = useCallback(async () => {
    try {
      setLoading(true);
      if (!createViewInput.name) {
        dispatchProjectError('Please enter a name for the view');
        return;
      }
      const payload = {
        name: createViewInput.name,
        description: createViewInput.description || createViewInput.generatedDescription,
        id: createViewInput.id,
        icon: createViewInput.icon,
        color: createViewInput.color,
        userIds: createViewInput.userIds,
        filters: createViewInput?.filters
      };

      const response = await updateView(payload, viewId);
      if (response) {
        await updateWorkspaceViewPreference(viewId, payload);
        await getViewList();
        rootStore.viewStore.setCurrentView(viewId);
        await history.push(ROUTES.VIEW_ROUTE.replace(':viewId', viewId));
        createViewInput.clear();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  }, [createViewInput, history, viewId]);

  const handleCancel = useCallback(() => {
    createViewInput.clear();
    if (viewId === 'new-view') {
      history.push(ROUTES.VIEWS);
    } else {
      history.push(ROUTES.VIEW_ROUTE.replace(':viewId', viewId));
    }
  }, [createViewInput, history, viewId]);

  // script for textarea height is increase according to content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px'; // reset to min
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [createViewInput?.description]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      createViewInput.setName(e.target.value);
      // Auto-resize the textarea
      const textarea = e.target;
      textarea.style.height = '26px';
      const scrollHeight = textarea.scrollHeight;
      if (scrollHeight > 26) {
        textarea.style.height = `${scrollHeight}px`;
      }
    },
    [createViewInput]
  );

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  const renderDisplayFilterDropdown = useMemo(() => {
    return <DisplayFilterDropdown isCreateView={true} />;
  }, []);

  const renderCreateViewButton = useMemo(() => {
    return (
      <Button
        title={viewId && viewId !== 'new-view' ? 'Save' : 'Create view'}
        variant={BUTTON_TYPE.PRIMARY}
        buttonType='submit'
        isLoading={loading}
        size='small'
      />
    );
  }, [loading, viewId]);

  return (
    <CardWrapper
      onSubmit={(e) => {
        e.preventDefault();
        if (viewId && viewId !== 'new-view') {
          onUpdateView();
        } else {
          onCreateView();
        }
      }}>
      <CardHeader>
        <TitleBar>
          <AddIconDiv>
            <AddIconDropdown
              isViewPage={true}
              size={isMobile ? 32 : 26}
              fontSize={isMobile ? '20px' : '16px'}
              iconSize={isMobile ? 20 : 16}
            />
          </AddIconDiv>

          {/* set edit condition */}
          <TaskTitle
            ref={titleTextareaRef}
            className='taskArea'
            placeholder='Task Title'
            autoFocus={true}
            value={createViewInput?.name}
            onChange={handleTitleChange}
            onKeyDown={handleTitleKeyDown}
          />
          {!isMobile && <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} onClick={handleCancel} size='small' />}
          {!isMobile && renderCreateViewButton}
        </TitleBar>
        <CardDescription>
          {true ? (
            <textarea
              ref={textareaRef}
              value={createViewInput?.description}
              onChange={(e) => createViewInput?.setDescription(e.target.value)}
              placeholder={createViewInput?.generatedDescription || 'Description (optional)'}
            />
          ) : (
            <span>{createViewInput?.description || 'No description provided.'}</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardBottomSection>
        <TabDisplay>
          <FilterChips
            onChangePriority={onChangePriority}
            onChangeStatus={onChangeStatus}
            onChangeProject={onChangeProject}
            onChangeDueDate={onChangeDueDate}
            onChangeAssign={onChangeAssign}
            onChangeUnassign={onChangeUnassign}
            onChangeCustomer={onChangeCustomer}
          />
          <Dropdown
            ref={dropdownRef}
            modalTitle='Filters'
            isMinWidth={150}
            onOutsideClick={() => setIsTaskFilterOpen(false)}
            content={
              <Chip
                size={CHIP_SIZE.SMALL}
                variant={CHIP_TYPE.FILL}
                leadingIconName='small-filters-icon'
                title={!isEditMode ? 'Filters' : ''}
                textColorType='secondary'
                onClick={() => setIsTaskFilterOpen(!isTaskFilterOpen)}
                isActive={isTaskFilterOpen}
              />
            }
            trigger='click'>
            <FilterDropdowns
              onChangeStatus={onChangeStatus}
              onChangePriority={onChangePriority}
              onChangeProject={onChangeProject}
              onChangeDueDate={onChangeDueDate}
              onChangeCustomer={onChangeCustomer}
              closeDropdown={() => dropdownRef.current?.closeDropdown()}
            />
          </Dropdown>
        </TabDisplay>
        {!isMobile && renderDisplayFilterDropdown}
      </CardBottomSection>
      {isMobile && (
        <ResponsiveButtonSection>
          {renderDisplayFilterDropdown}
          {renderCreateViewButton}
        </ResponsiveButtonSection>
      )}
    </CardWrapper>
  );
};

export default observer(ViewCard);
